import {
  LayoutDashboard,
  BarChart3,
  Building2,
  LayoutGrid,
  Users,
  ShieldCheck,
  FolderKanban,
  DoorOpen,
  Video,
  Disc,
  ListChecks,
  MessageSquare,
  Plug,
  CreditCard,
  ScrollText,
  Clock,
  ServerCog,
  MonitorDown,
  MonitorSmartphone,
  Settings,
  ShieldAlert,
  HardDrive,
  Bot,
  BookText,
  Sparkles,
  UsersRound,
  MessagesSquare,
  FolderOpen,
  Bug,
  BellRing,
  Megaphone,
  Smartphone,
  type LucideIcon,
} from 'lucide-react';
import type { Action } from '@/lib/permissions';

export interface NavItem {
  /** Absolute route path. */
  path: string;
  label: string;
  icon: LucideIcon;
  /** Permission required to see/visit this route. */
  action: Action;
  /** Short description for the command palette. */
  description: string;
  /**
   * FIX(admin-perms-recordings): other ways into the same section, tried in
   * order when `action` is not granted — so a section whose pages are gated by
   * different permissions still shows for anyone who can reach ONE of them,
   * and links to a page they can actually open. Before: one action per item,
   * so the Recordings nav hid from someone who could only reach its config
   * page. After: see `reachableNavItems`.
   */
  fallbacks?: { path: string; action: Action }[];
  /**
   * FIX(admin-nav-not-built-badge): Billing and Integrations are mock-backed /
   * not-built-yet surfaces (see the admin-mocks convention) with no other
   * visual signal in the nav. Before: no indication the surface is still on
   * mocks. After: an optional inline status badge next to the label (e.g.
   * "Not built"), shown in both the desktop sidebar and mobile drawer via the
   * shared `SidebarNav`, hidden when the sidebar is collapsed. Variant follows
   * `components/ui/badge.tsx`.
   */
  badge?: { label: string; variant?: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline' };
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      {
        path: '/dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        action: 'dashboard:view',
        description: 'Operational KPIs and incidents',
      },
      {
        path: '/analytics',
        label: 'Analytics',
        icon: BarChart3,
        action: 'dashboard:analytics',
        description: 'Platform usage charts',
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        path: '/workspaces',
        label: 'Workspaces',
        icon: Building2,
        action: 'workspaces:view',
        description: 'Tenants, plans, usage, members',
      },
      {
        path: '/floors',
        label: 'Floors',
        icon: LayoutGrid,
        action: 'floors:view',
        description: 'Spatial office floor plans (floors)',
      },
      {
        path: '/users',
        label: 'Users',
        icon: Users,
        action: 'users:view',
        description: 'Accounts across all workspaces',
      },
      {
        path: '/roles',
        label: 'Roles',
        icon: ShieldCheck,
        action: 'roles:view',
        description: 'Roles and the permission matrix',
      },
      {
        path: '/projects',
        label: 'Projects',
        icon: FolderKanban,
        action: 'projects:view',
        description: 'Tracker projects, leads, members',
      },
      {
        path: '/rooms',
        label: 'Rooms',
        icon: DoorOpen,
        action: 'rooms:view',
        description: 'Live rooms and sessions',
      },
      {
        path: '/meetings',
        label: 'Meetings',
        icon: Video,
        action: 'meetings:view',
        description: 'Scheduled and completed meetings',
      },
      {
        path: '/recordings',
        label: 'Recordings',
        icon: Disc,
        action: 'recordings:view',
        description: 'Recordings, retention, playback',
        // The configuration page is `params.manage`, not `meetings.view`.
        fallbacks: [{ path: '/recordings/config', action: 'recordings:retention' }],
      },
      {
        path: '/tasks',
        label: 'Tasks',
        icon: ListChecks,
        action: 'tasks:view',
        description: 'Tracker work items across projects',
      },
      {
        path: '/messaging',
        label: 'Messaging',
        icon: MessageSquare,
        action: 'messaging:view',
        description: 'Channels, DMs, compliance',
      },
      {
        path: '/docs',
        label: 'Docs',
        icon: BookText,
        action: 'docs:view',
        description: 'Wiki spaces, pages, sharing, trash',
      },
      {
        path: '/errors',
        label: 'Errors',
        icon: Bug,
        action: 'errors:view',
        description: 'Runtime errors and 5xx captured from users',
      },
    ],
  },
  {
    label: 'Notifications',
    items: [
      {
        path: '/notifications',
        label: 'Deliveries',
        icon: BellRing,
        action: 'notifications:view',
        description: 'Every push, email and in-app notification: who got it and who did not',
      },
      {
        path: '/notifications/broadcasts',
        label: 'Broadcasts',
        icon: Megaphone,
        action: 'notifications:view',
        description: 'Send a push, email or in-app announcement to users, orgs or everyone',
      },
      {
        path: '/notifications/devices',
        label: 'Push devices',
        icon: Smartphone,
        action: 'notifications:view',
        description: 'Registered mobile push devices, test push, remove',
      },
    ],
  },
  {
    label: 'AI',
    items: [
      {
        path: '/agents/catalog',
        label: 'AI agent catalog',
        icon: Sparkles,
        action: 'agents:view',
        description: 'AI employees: persona, tools, routing, hires; create, edit, disable',
      },
      {
        path: '/agents/org',
        label: 'Org AI employees',
        icon: UsersRound,
        action: 'agents:view',
        description: 'AI employees hired by each organisation; hire, edit, fire',
      },
      {
        path: '/ai/conversations',
        label: 'AI conversations',
        icon: MessagesSquare,
        action: 'aiOversight:view',
        description: 'Every AI chat across organisations, with transcripts',
      },
      {
        path: '/ai/projects',
        label: 'AI projects',
        icon: FolderOpen,
        action: 'aiOversight:view',
        description: 'AI Workspace projects across organisations',
      },
    ],
  },
  {
    label: 'Commerce & Integrations',
    items: [
      {
        path: '/integrations',
        label: 'Integrations',
        icon: Plug,
        action: 'integrations:view',
        description: 'Connected services and sync',
        badge: { label: 'Not built', variant: 'warning' },
      },
      {
        path: '/billing',
        label: 'Billing',
        icon: CreditCard,
        action: 'billing:view',
        description: 'Plans, invoices, credits',
        badge: { label: 'Not built', variant: 'warning' },
      },
    ],
  },
  {
    label: 'Trust & Platform',
    items: [
      {
        path: '/audit',
        label: 'Audit log',
        icon: ScrollText,
        action: 'audit:view',
        description: 'Admin & security events',
      },
      {
        path: '/attendance',
        label: 'Attendance',
        icon: Clock,
        action: 'attendance:view',
        description: 'Room occupancy and in-office presence',
      },
      {
        // Attendance has no group of its own — it lives in Trust & Platform — so
        // "Client policy" sits immediately after it, where an operator looking at
        // attendance data will next look for why some of it is untracked.
        path: '/client-policy',
        label: 'Client policy',
        icon: MonitorSmartphone,
        // The endpoints behind this page are gated `workspaces.manage`
        // server-side; SERVER_PERM_ACTIONS maps that server perm to
        // `workspaces:edit`, so that is the action to gate the nav on.
        action: 'workspaces:edit',
        description: 'Which apps count as tracked time, per organisation and per member',
      },
      {
        path: '/security',
        label: 'Security',
        icon: ShieldAlert,
        action: 'security:view',
        description: 'Login attempts, IP records, request logs',
      },
      {
        path: '/files',
        label: 'Files',
        icon: HardDrive,
        action: 'files:view',
        description: 'Global file registry and storage usage',
      },
      {
        path: '/providers',
        label: 'AI Providers',
        icon: Bot,
        action: 'providers:view',
        description: 'AI model providers, API keys, and allowlist',
      },
      {
        path: '/releases',
        label: 'Desktop Releases',
        icon: MonitorDown,
        action: 'releases:view',
        description: 'Desktop app installers, publish a version, roll back',
      },
      {
        path: '/system',
        label: 'System',
        icon: ServerCog,
        action: 'system:view',
        description: 'Health, queues, feature flags',
      },
      {
        path: '/settings',
        label: 'Settings',
        icon: Settings,
        action: 'settings:view',
        description: 'SSO, IP allowlist, preferences',
      },
    ],
  },
];

export const ALL_NAV_ITEMS: NavItem[] = NAV.flatMap((g) => g.items);

/**
 * The items this user can reach, each resolved to the first of its `path` /
 * `fallbacks` they are allowed to open (see `NavItem.fallbacks`).
 */
export function reachableNavItems(items: NavItem[], can: (action: Action) => boolean): NavItem[] {
  return items.flatMap((item) => {
    if (can(item.action)) return [item];
    const alt = item.fallbacks?.find((f) => can(f.action));
    return alt ? [{ ...item, path: alt.path, action: alt.action }] : [];
  });
}

export function findNavItem(pathname: string): NavItem | undefined {
  // The longest matching path wins, so /notifications/devices is "Push devices", not the
  // "Deliveries" item whose path (/notifications) is its prefix.
  let best: NavItem | undefined;
  for (const i of ALL_NAV_ITEMS) {
    if ((pathname === i.path || pathname.startsWith(`${i.path  }/`)) && (!best || i.path.length > best.path.length)) {
      best = i;
    }
  }
  return best;
}
